import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Box,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import Webcam from "react-webcam";
import "./business-card-scanner-dialog.scss";
import { styled } from "@mui/material/styles";
import { ReactComponent as WebcamBorderLandscape } from "../../../../assets/icons/webcam_border_landscape.svg";
import { ReactComponent as WebcamBorderPortrait } from "../../../../assets/icons/webcam_border_portrait.svg";
import { ReactComponent as UploadIcon } from "../../../../assets/icons/upload_icon.svg";
import {
  AlertType,
  BusinessCardType,
  CameraPermissionType,
} from "../../../../utils/util-types";
import { parseBusinessCard, readText } from "./card-scanner-helper";
import { ALERT_TYPE, CAMERA_PERMISSION } from "../../../../utils/constants";

const videoConstraints = {
  facingMode: { ideal: "environment" }, // back camera
  width: 1280,
  height: 720,
};
const ORIENTATION = {
  LANDSCAPE: "landscape",
  PORTRAIT: "portrait",
};
const IMAGE_SOURCE = {
  CAMERA: "camera",
  FILE: "file",
};
interface WebcamHandle {
  getScreenshot: () => string | null;
}

interface BusinessCardScannerDialogProps {
  open: boolean;
  onClose: () => void;
  onScanComplete: (value: BusinessCardType) => void;
  setAlertOpen: (isOpen: boolean) => void;
  setAlertMessage: (message: string) => void;
  setAlertType: (type: AlertType) => void;
}
const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const BusinessCardScannerDialog: React.FC<BusinessCardScannerDialogProps> = ({
  open,
  onClose,
  onScanComplete,
  setAlertOpen,
  setAlertMessage,
  setAlertType,
}) => {
  const [cameraReady, setCameraReady] = useState(false);
  const webcamRef = useRef<WebcamHandle | null>(null);
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState<CameraPermissionType>(
    CAMERA_PERMISSION.PENDING,
  );
  const [cameraError, setCameraError] = useState<string>("");
  const [currentOrientation, setCurrentOrientation] = useState<string>(
    ORIENTATION.LANDSCAPE,
  );

  const capture = async () => {
    const screenshot = webcamRef.current?.getScreenshot();
    if (!screenshot) return;
    parseInfo(screenshot, IMAGE_SOURCE.CAMERA);
  };

  useEffect(() => {
    let timerId: any = null;
    if (!open) {
      timerId = setTimeout(() => {
        setCurrentOrientation(ORIENTATION.LANDSCAPE);
      }, 500);
      return;
    }
    const id = setInterval(() => {
      capture();
    }, 2000);

    return () => {
      clearInterval(id);
      clearTimeout(timerId);
      setLoading(false);
      setCameraReady(false);
    };
  }, [open]);

  const isValidEmail = (email: string) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const parseInfo = async (imageString: string, imageSource: string) => {
    const ocrText = await readText(imageString);
    const parsed = parseBusinessCard(ocrText);

    if (parsed.name && parsed.email && !isValidEmail(parsed.email)) {
      setLoading(false);
      setAlertMessage("Email id scanning failed. Please retry.");
      setAlertType("error");
      setAlertOpen(true);
      return;
    }
    if (parsed.name && parsed.email) {
      setLoading(false);
      onScanComplete(parsed);
    } else if (imageSource === IMAGE_SOURCE.FILE) {
      setAlertMessage("Invalid image detected. Please scan a valid business card.");
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }else{
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const onFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const validFiles = Array.from(files).filter((file) =>
      ["image/png", "image/jpeg"].includes(file.type),
    );

    if (validFiles.length !== files.length) {
      setAlertMessage("Only PNG, JPG, or JPEG images are allowed.");
      setAlertType(ALERT_TYPE.ERROR);
      setAlertOpen(true);
      event.target.value = ""; // reset input
      return;
    } else {
      const base64: any = await new Promise((res) => {
        const reader = new FileReader();
        reader.onloadend = () => res(reader.result);
        reader.readAsDataURL(validFiles[0]);
      });
      //const base64Images: any = await toBase64(validFiles[0]);
      setLoading(true);
      parseInfo(base64, IMAGE_SOURCE.FILE);
    }
  };
  const onUserMediaError = (error: any) => {
    if (error.name === "NotAllowedError") {
      setAlertMessage("User denied camera permission.");
      setCameraError(
        "Denied camera permission. Please allow camera permission from setting",
      );
      setPermission(CAMERA_PERMISSION.DENIED);
    } else if (error.name === "NotFoundError") {
      setAlertMessage("Camera device not available.");
      setCameraError("Camera device not available.");
      setPermission(CAMERA_PERMISSION.NOT_AVAILABLE);
    } else if (error.name === "NotReadableError") {
      setAlertMessage("Camera already in use.");
      setCameraError("Camera already in use.");
      setPermission(CAMERA_PERMISSION.IN_USE);
    }
    setAlertType(ALERT_TYPE.ERROR);
    setAlertOpen(true);
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      className="card-scanner-dialog"
      slotProps={{
        paper: {
          sx: {
            height: "100vh",
            maxHeight: "100vh",
            "& .MuiDialog-paperScrollPaper": {
              height: "80vh",
              maxHeight: "80vh",
            },
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        },
      }}
    >
      <DialogTitle className="dialog-title">
        {/* Scan Card */}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div className="dialog-content">
          <div className="camera-container-parent">
            <div
              className={`camera-container ${currentOrientation === ORIENTATION.LANDSCAPE ? "camera-landscape" : "camera-portrait"}`}
            >
              {currentOrientation === ORIENTATION.LANDSCAPE && (
                <WebcamBorderLandscape className="camera-landscape" />
              )}
              {currentOrientation === ORIENTATION.PORTRAIT && (
                <WebcamBorderPortrait className="camera-portrait" />
              )}

              <Webcam
                className={`webcam-${currentOrientation === ORIENTATION.LANDSCAPE ? "landscape" : "portrait"}`}
                ref={webcamRef}
                audio={false}
                muted
                screenshotQuality={1}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                onUserMediaError={onUserMediaError}
                onUserMedia={() => {
                  setPermission(CAMERA_PERMISSION.GRANTED);
                  setCameraReady(true);
                }}
                style={{
                  display: cameraReady ? "block" : "none",
                  inset: 2,
                }}
              />

              <div className="scan-overlay">
                {cameraReady && (
                  <div className="scan-box">
                    <div className="scan-line" />
                  </div>
                )}
                {permission !== CAMERA_PERMISSION.GRANTED &&
                  permission !== CAMERA_PERMISSION.PENDING && (
                    <span className="camera-error">{cameraError}</span>
                  )}
              </div>
            </div>
          </div>

          <div className="orientation">
            <div
              className={`landscape-parent ${currentOrientation === ORIENTATION.LANDSCAPE ? "orientation-selected" : ""}`}
            >
              <button
                className="landscape"
                onClick={() => {
                  //capture();
                  setCurrentOrientation(ORIENTATION.LANDSCAPE);
                }}
              />
            </div>
            <div
              className={`portrait-parent ${currentOrientation === ORIENTATION.PORTRAIT ? "orientation-selected" : ""}`}
            >
              <button
                className="portrait"
                onClick={() => {
                  setCurrentOrientation(ORIENTATION.PORTRAIT);
                }}
              />
            </div>
          </div>
          <div className="div-orientation-details">
            <span className="text-orientation">
              {`${currentOrientation === ORIENTATION.LANDSCAPE ? "Landscape" : "Portrait"}`}
              &nbsp; Mode:&nbsp;
            </span>
            <span className="text-orientation-align">
              Align the card within the frame
            </span>
          </div>

          <div className="div-upload">
            <Button
              component="label"
              role={undefined}
              tabIndex={-1}
              disabled={loading}
              startIcon={<UploadIcon className="primary_color" />}
              className="card-upload-btn "
              sx={{
                textTransform: "none",
              }}
            >
              Upload from device
              <VisuallyHiddenInput
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                onChange={onFileUpload}
              />
            </Button>
          </div>
        </div>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
          gap: 2,
          paddingBottom: 3,
        }}
        className="buttons-group"
      ></DialogActions>
    </Dialog>
  );
};

export default BusinessCardScannerDialog;
